import AuthorizationError from '@/common/utils/errors/AuthorizationError';
import { rootDir } from '@/common/utils/pathPrefix.util';
import CancellationError from '@/common/utils/errors/CancellationError';
import NetworkError from '@/common/utils/errors/NetworkError';
import JsonParseError from '@/common/utils/errors/JsonParseError';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import ValidationError from '@/common/utils/errors/ValidationError';
import InternalServerError from '@/common/utils/errors/InternalServerError';
import ServerError from '@/common/utils/errors/ServerError';
import { throttle } from 'lodash-es';
import { useEventBus } from '@vueuse/core';
import isSelfService from '@/common/utils/isSelfService';
import RequestController from '@/common/utils/requestController';
import { trackNetworkError, trackBackendError } from '@/common/utils/telemetry/telemetryErrorTracking';
import { trackFileUploadStart, trackFileUploadComplete } from '@/common/utils/telemetry/telemetryFileTransferMonitor';
import extractFileInfo from '@/common/utils/fileInfoExtractor';
import { OPENSTACK_AUTH_RECEIPT_HEADER } from '@/common/constants/auth.constants';

interface StoreAdapter {
  getScopedToken: () => string,
  unauthorize: () => Promise<void>,
  checkAccessDisabled: () => Promise<void>,
  checkReconfiguration: () => Promise<void>
}

type Config = {
  headers: FetchHeaders | Headers & XAuthorizationHeaders,
  url: string;
  path: string;
  params: any;
  abortController?: AbortController;
}

type ResponseData = CommonObject | string;

export const bus = useEventBus<CommonObject>('fetchResponse');

export default class CreateFetch extends RequestController {
  readonly defaultHeaders: FetchHeaders = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  storeAdapter?: StoreAdapter;

  setAdapter(storeAdapter: StoreAdapter) {
    this.storeAdapter = storeAdapter;
  }

  setAuthorizationHeaders(options): XAuthorizationHeaders {
    const scopedToken = this.storeAdapter?.getScopedToken();
    return {
      'X-Session-Id': isSelfService() ? 1 : 0,
      ...(!options.unscopedRequest
        && scopedToken
        && { 'X-Auth-Token': scopedToken }
      )
    };
  }

  async unauthorize(): Promise<void> {
    return this.storeAdapter?.unauthorize();
  }

  async authGuard(response, data, { skipAuthGuard, forceAuthGuard }: AuthGuardParams): Promise<any> {
    if (response.status === 401) {
      if (isSelfService()) {
        if (skipAuthGuard) {
          return response;
        } else if (forceAuthGuard) {
          await this.unauthorize();
          throw new AuthorizationError(response, data);
        }

        const totpReceipt = response.headers?.get(OPENSTACK_AUTH_RECEIPT_HEADER);
        if (totpReceipt) {
          throw new AuthorizationError(response, data);
        }

        try {
          await this.storeAdapter?.checkAccessDisabled();
        } catch (e) {
          if (e instanceof AuthorizationError) {
            await this.unauthorize();
            throw new AuthorizationError(response, data);
          }
        }
      } else {
        await this.unauthorize();
        throw new AuthorizationError(response, data);
      }
    }

    return response;
  }

  async reconfigurationHook(response: Response): Promise<Response> {
    const errorPathName = '/service-unavailable';
    const reconfigurationState = response.headers.get('X-Reconfiguration-State');
    if (reconfigurationState) {
      if (isSelfService()) {
        if (window.location.pathname !== errorPathName) {
          window.location.pathname = errorPathName;
        }
      } else {
        await this.storeAdapter?.checkReconfiguration();
      }
    }
    return response;
  }

  constructUri(path: UriPath, rootPath: string, params: FetchRequestParams): QueryParamsString {
    let url = `${rootPath}${encodeURI(path)}`;
    if (params && Object.keys(params).length > 0) {
      const queryParams = Object.keys(params).reduce((result: string[], key: string) => {
        const param = params[key];
        if (param === undefined) {
          return result;
        }
        if (typeof params[key] === 'object') {
          return result.concat(`${key}=${encodeURIComponent(JSON.stringify(param))}`);
        }
        return result.concat(`${key}=${encodeURIComponent(param)}`);
      }, []);
      if (queryParams.length) {
        url += `?${queryParams.join('&')}`;
      }
    }
    return url;
  }

  async getData(response): Promise<ResponseData> {
    const text = await response.text();
    let body;
    const contentType: string = response.headers.get('content-type') ?? '';
    if (text.length && /application\/json/i.test(contentType)) {
      try {
        body = JSON.parse(text);
      } catch (err) {
        throw new JsonParseError(text, response.status);
      }
    } else {
      body = text;
    }
    return body;
  }

  makeRequest(path: UriPath, { rootPath = rootDir, params = {}, headers = {}, ...restOptions }): Promise<FetchResponse> {
    let data;
    rootPath = rootPath.replace(/\/$/, '');
    const url = this.constructUri(path, rootPath, params);

    const config: Config = {
      url,
      path,
      params,
      ...restOptions,
      headers: {
        ...this.defaultHeaders,
        ...headers,
        ...this.setAuthorizationHeaders(restOptions),
      }
    };

    return this.makeFetch(config)
      .catch((err) => {
        if (err.name === 'AbortError') {
          throw new CancellationError(err);
        }
        const networkError = new NetworkError(err);
        trackNetworkError(networkError, url, {
          request_method: restOptions.method,
          request_headers: config.headers
        });
        throw networkError;
      })
      .then(async (res) => {
        data = await this.getData(res);
        return this.authGuard.call(this, res, data, restOptions as AuthGuardParams);
      })
      .then(this.reconfigurationHook.bind(this))
      .then(async (response) => {
        if (response.status >= 200 && response.status < 400) {
          if (restOptions.method !== 'get') {
            bus.emit(data);
          }
          return Promise.resolve({ data, response, request: restOptions.body, params });
        }
        if (response.status >= 400 && response.status < 500) {
          const error = response.status === 404
            ? new NotFoundError(response, data)
            : new ValidationError(response, data);

          trackBackendError(error, response.status, url, data?.error?.err_code, {
            request_method: restOptions.method,
            response_data: data,
            error_category: '4xx'
          });

          throw error;
        }

        const serverError = new InternalServerError(response, data);
        trackBackendError(serverError, response.status, url, data?.error?.err_code, {
          request_method: restOptions.method,
          response_data: data,
          error_category: '5xx'
        });
        throw serverError;
      });
  }

  makeFetch({ url, headers, path, params, abortController, ...options }: Config): Promise<Response> {
    const requestController = this.createRequestController(abortController);

    return fetch(url, {
      mode: 'cors',
      credentials: 'same-origin',
      ...options,
      headers,
      signal: requestController.controller.signal
    }).finally(() => {
      this.removeController(requestController);
    });
  }

  get<T = any>(path: UriPath, params?: FetchRequestParams, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, params, method: 'get' });
  }

  post<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'post' });
  }

  put<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'put' });
  }

  patch<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'PATCH' });
  }

  delete<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'delete' });
  }

  upload(path: UriPath, body: (Document | XMLHttpRequestBodyInit | null), options: any, onProgress?: (process: ProgressEvent, xhr: XMLHttpRequest) => void): Promise<UploadResponse> {
    const rootPath: string = rootDir.replace(/\/$/, '');

    return new Promise((resolve, reject) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.responseType = 'text';
      xhr.open(options.method, rootPath + path);
      xhr.timeout = 0;

      const { fileName, fileSize } = extractFileInfo(body, options);
      const uploadStartTime = trackFileUploadStart({
        fileName,
        fileSize,
        metadata: {
          upload_path: path,
          request_method: options.method
        }
      });

      const headers: XAuthorizationHeaders & FetchHeaders = {
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
        ...this.setAuthorizationHeaders(options),
      };

      Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]));

      xhr.onload = (e: ProgressEvent) => {
        const response: XMLHttpRequest = e.target as XMLHttpRequest;

        const { responseText } = response;
        let responseBody: any = {};
        const contentType: string = response.getResponseHeader('content-type') ?? '';
        if (responseText.length && /application\/json/i.test(contentType)) {
          try {
            responseBody = JSON.parse(responseText);
          } catch (err) {
            reject(new JsonParseError(responseText, response.status));
          }
        }

        this.authGuard(response, responseBody, options as AuthGuardParams).catch(err => reject(err));

        const result: UploadResponse = { data: responseBody, response };
        if (response.status >= 200 && response.status < 400) {
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: true,
            additionalProperties: {
              status_code: response.status,
              response_size: responseText.length
            }
          });
          bus.emit(responseBody);
          resolve(result);
        } else if (response.status >= 400 && response.status < 500) {
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '4xx'
            }
          });
          reject(new ValidationError(response, responseBody));
        } else {
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '5xx'
            }
          });
          reject(new ServerError(response, responseBody));
        }
      };

      let timeoutId: ReturnType<typeof setTimeout>;
      const onError = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Network error',
          additionalProperties: {
            error_type: 'network_error'
          }
        });
        reject(new NetworkError(err));
      };

      const onAbort = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Upload cancelled',
          additionalProperties: {
            error_type: 'cancellation'
          }
        });
        reject(new CancellationError(err));
      };

      xhr.onerror = onError;
      xhr.onabort = onAbort;

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = throttle((progress: ProgressEvent) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            trackFileUploadComplete({
              startTime: uploadStartTime,
              fileName,
              fileSize,
              success: false,
              errorMessage: 'Upload timeout',
              additionalProperties: {
                error_type: 'timeout',
                timeout_ms: options.uploadTimeout || 60000
              }
            });
            onError(progress);
          }, options.uploadTimeout || 60000);
          onProgress(progress, xhr);
        }, 100);
        xhr.onloadend = () => {
          clearTimeout(timeoutId);
        };
      }

      xhr.send(body);
    });
  }
}
